import React, { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';

const HOLD_MS = 1650;   // how long the animated splash holds before revealing the app
const EXIT_MS = 700;    // reveal transition duration

// Animated splash that takes over seamlessly from the static native splash
// (same brand blue + centered B), plays a gentle breath + sonar ripple, then
// eases away. Preview in any browser with ?splashPreview=1
export const NativeSplash = () => {
  const isNative = Capacitor.isNativePlatform();
  const isPreview = typeof window !== 'undefined' && window.location.search.includes('splashPreview=1');
  const [phase, setPhase] = useState('hold'); // hold → exit → gone

  useEffect(() => {
    if (!isNative && !isPreview) return;
    if (isNative) {
      // Crossfade the static native splash into this animated overlay
      requestAnimationFrame(() => {
        SplashScreen.hide({ fadeOutDuration: 250 }).catch(() => {});
      });
    }
    const exitTimer = setTimeout(() => setPhase('exit'), HOLD_MS);
    const goneTimer = setTimeout(() => setPhase('gone'), HOLD_MS + EXIT_MS);
    return () => { clearTimeout(exitTimer); clearTimeout(goneTimer); };
  }, [isNative, isPreview]);

  if ((!isNative && !isPreview) || phase === 'gone') return null;

  return (
    <div
      data-testid="native-splash-overlay"
      style={{
        position: 'fixed', inset: 0, zIndex: 2147483000,
        background: '#2763EB',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        opacity: phase === 'exit' ? 0 : 1,
        transform: phase === 'exit' ? 'scale(1.035)' : 'scale(1)',
        transition: `opacity ${EXIT_MS}ms cubic-bezier(0.22,1,0.36,1), transform ${EXIT_MS}ms cubic-bezier(0.22,1,0.36,1)`,
        pointerEvents: phase === 'exit' ? 'none' : 'auto',
      }}
    >
      <style>{`
        @keyframes bfSplashBreath {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes bfSplashRipple {
          0% { transform: scale(0.55); opacity: 0.35; }
          80% { opacity: 0.06; }
          100% { transform: scale(1.9); opacity: 0; }
        }
        @keyframes bfSplashWordmark {
          0% { opacity: 0; transform: translateY(10px); letter-spacing: 0.35em; }
          100% { opacity: 0.92; transform: translateY(0); letter-spacing: 0.14em; }
        }
      `}</style>

      {/* Logo + sonar ripples */}
      <div style={{ position: 'relative', width: 132, height: 132 }}>
        {[0, 1].map((i) => (
          <div key={i} style={{
            position: 'absolute', inset: -34,
            borderRadius: '50%',
            border: '1.5px solid rgba(255,255,255,0.5)',
            animation: `bfSplashRipple 2.4s cubic-bezier(0.25,0.6,0.35,1) ${i * 1.2}s infinite`,
            opacity: 0,
          }} />
        ))}
        <img
          src="/logo-blue-512.png"
          alt=""
          draggable={false}
          style={{
            width: '100%', height: '100%',
            animation: 'bfSplashBreath 2.3s ease-in-out infinite',
            userSelect: 'none',
          }}
        />
      </div>

      {/* Wordmark */}
      <div style={{
        marginTop: 26,
        color: '#fff',
        fontSize: 15,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.14em',
        animation: 'bfSplashWordmark 900ms cubic-bezier(0.22,1,0.36,1) 250ms both',
      }}>
        BookingFlows
      </div>
    </div>
  );
};

export default NativeSplash;
